import { createContext, useCallback, useContext, useEffect, useState, useMemo } from 'react';
import toast from 'react-hot-toast';
import { biedOngedaanAan } from '../../utils/ongedaan';
import { useParams } from 'react-router-dom';
import { useMsal } from '@azure/msal-react';
import { AI_ENGINE_URL, BACKEND_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';
import { useMailNotities } from './useMailNotities';

/**
 * Alle projectdata van het projectoverzicht op één plek: laden, verversen en
 * muteren. De provider is 1-op-1 verplaatst uit ProjectOverview.jsx zodat de
 * tabpanelen hun data via useProjectData() kunnen pakken in plaats van via
 * dertig props. Pure UI-state (actieve tab, weergaves, dialogs) blijft bij de
 * component die hem gebruikt.
 */
const ProjectDataContext = createContext(null);

export function ProjectDataProvider({ children, projectId }) {
  // projectId: voor gebruik buiten /projects/:id (bv. het zijpaneel op de callsheet).
  const params = useParams();
  const id = projectId || params.id;
  const { accounts } = useMsal();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [project, setProject] = useState(null);
  const [tasks, setTasks] = useState([]);
  const [deliverables, setDeliverables] = useState([]);
  const [notes, setNotes] = useState([]);
  const [transcripts, setTranscripts] = useState([]);
  const [meetingSuggesties, setMeetingSuggesties] = useState([]);
  const [quitclaims, setQuitclaims] = useState([]);
  const [draaidagen, setDraaidagen] = useState([]);
  const [callsheets, setCallsheets] = useState([]);
  const [eindeDraaidagen, setEindeDraaidagen] = useState([]);
  const [selectedTask, setSelectedTask] = useState(null); // For task detail dialog
  const [autoEditTaskDate, setAutoEditTaskDate] = useState(false); // Auto-open date editor in dialog
  const [newNoteText, setNewNoteText] = useState(''); // Inline note input
  const [savingNote, setSavingNote] = useState(false);
  const [newTaskText, setNewTaskText] = useState(''); // Inline task input
  const [savingTask, setSavingTask] = useState(false);
  const [frameioVideos, setFrameioVideos] = useState([]);
  const [downloadingVideoId, setDownloadingVideoId] = useState(null);
  const [qaReviews, setQaReviews] = useState([]);
  const [personen, setPersonen] = useState([]); // Voor het toewijzen van een uitvoerder

  const userEmail = accounts?.[0]?.username || '';

  // Actieve medewerkers ophalen zodat je in de taakdetails een uitvoerder kunt kiezen.
  useEffect(() => {
    let geannuleerd = false;
    (async () => {
      try {
        const resp = await authFetch(`${BACKEND_URL}/Api/personen`);
        if (!resp.ok) return;
        const data = await resp.json();
        if (!geannuleerd) setPersonen(data.records || []);
      } catch (err) {
        console.error('Personen ophalen mislukt:', err);
      }
    })();
    return () => { geannuleerd = true; };
  }, []);

  // Handle Frame.io video download
  const handleVideoDownload = async (e, video) => {
    e.stopPropagation(); // Don't open player when clicking download
    setDownloadingVideoId(video.frameio_asset_id);
    try {
      const resp = await authFetch(`${AI_ENGINE_URL}/api/v1/frameio/videos/${video.frameio_asset_id}/download-url`);
      if (!resp.ok) throw new Error('Download URL niet beschikbaar');
      const data = await resp.json();
      // Trigger browser download via hidden link
      const a = document.createElement('a');
      a.href = data.download_url;
      a.download = data.filename || video.filename;
      a.target = '_blank';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
    } catch (err) {
      console.error('Download error:', err);
      toast.error('Kon video niet downloaden');
    } finally {
      setDownloadingVideoId(null);
    }
  };

  // Handle quick add note
  const handleAddNote = async () => {
    if (!newNoteText.trim() || savingNote) return;

    const noteText = newNoteText.trim();
    setSavingNote(true);
    setNewNoteText('');
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/project-notes`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          projectId: id,
          title: noteText.split('\n')[0].substring(0, 50) || 'Notitie',
          notes: noteText,
          source: 'Manual',
        }),
      });

      if (!response.ok) throw new Error('Kon notitie niet opslaan');
      const data = await response.json();

      // Add the created note to local state immediately (optimistic)
      if (data.record) {
        setNotes(prev => [data.record, ...prev]);
      }
      toast.success('Notitie toegevoegd');

      // Background refresh after delay (Airtable eventual consistency)
      setTimeout(() => loadData(), 3000);
    } catch (err) {
      console.error('Error adding note:', err);
      setNewNoteText(noteText); // Restore text on error
      toast.error('Fout bij opslaan notitie');
    } finally {
      setSavingNote(false);
    }
  };

  // Handle update note (onderwerp + inhoud)
  const handleUpdateNote = async (recordId, { title, notes: body }) => {
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/project-notes/${recordId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ projectId: id, title, notes: body }),
      });
      if (!response.ok) throw new Error('Kon notitie niet bijwerken');

      setNotes(prev => prev.map(n => (
        n.id === recordId
          ? { ...n, fields: { ...n.fields, Onderwerp: title, Notes: body } }
          : n
      )));
      toast.success('Notitie bijgewerkt');
    } catch (err) {
      console.error('Error updating note:', err);
      toast.error('Fout bij bijwerken notitie');
      throw err;
    }
  };

  // Handle delete note — de bevestiging zit in NotesPanel (MUI-dialog),
  // dus hier geen window.confirm meer.
  const handleDeleteNote = async (recordId) => {
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/project-notes/${recordId}?projectId=${id}`, {
        method: 'DELETE',
      });
      if (!response.ok) throw new Error('Kon notitie niet verwijderen');
      setNotes(prev => prev.filter(n => n.id !== recordId));
      toast.success('Notitie verwijderd');
    } catch (err) {
      console.error('Error deleting note:', err);
      toast.error('Fout bij verwijderen notitie');
    }
  };

  // Een taak echt weg (Lars: moest tot nu toe handmatig in Airtable). De
  // bevestiging zit in de taakdialoog.
  const handleTaskDelete = async (taskId) => {
    const response = await authFetch(`${BACKEND_URL}/Api/tasks/${taskId}`, { method: 'DELETE' });
    if (!response.ok) {
      const error = await response.json().catch(() => ({}));
      throw new Error(error.error?.message || error.error || 'Kon taak niet verwijderen');
    }
    setTasks(prev => prev.filter(t => t.id !== taskId));
    setSelectedTask(prev => (prev && prev.id === taskId ? null : prev));
    toast.success('Taak verwijderd');
  };

  // Handle quick add task (inline input)
  const handleAddQuickTask = async (extra = {}) => {
    if (!newTaskText.trim() || savingTask) return;

    setSavingTask(true);
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/tasks`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          NaamTaak: newTaskText.trim(),   // backend-veldnaam (was 'title')
          Project: [id],                  // linked record (was 'projectId')
          // Uitklapbare extra's (Joris, 1 sep): uitvoerder/datum/fase. Zonder
          // gekozen uitvoerder blijft de maker zelf de assignee.
          ...(extra.assigneeId ? { Assignee: [extra.assigneeId] } : { assigneeEmail: userEmail }),
          ...(extra.type ? { Type: extra.type } : {}),
          ...(extra.datum ? { StartDateUitvoerder: extra.datum, EndDateUitvoerder: extra.datum } : {}),
        }),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error || err.details?.error?.message || 'Kon taak niet aanmaken');
      }

      setNewTaskText('');
      loadData(); // Refresh to show new task
      toast.success('Taak toegevoegd');
    } catch (err) {
      console.error('Error adding task:', err);
      toast.error('Fout bij aanmaken taak');
    } finally {
      setSavingTask(false);
    }
  };

  // Count tasks per deliverable
  const tasksPerDeliverable = useMemo(() => {
    const counts = {};
    tasks.forEach(task => {
      const deliverableIds = task.fields?.Deliverable || [];
      deliverableIds.forEach(delId => {
        counts[delId] = (counts[delId] || 0) + 1;
      });
    });
    return counts;
  }, [tasks]);

  // Handle task update (from timeline drag & drop or edit)
  const handleTaskUpdate = async (taskId, updates) => {
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/tasks/${taskId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates),
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.error?.message || 'Kon taak niet bijwerken');
      }

      // Airtable geeft het bijgewerkte record terug MÉT herberekende lookups
      // ('Voornaam Assignee', 'Functie (from Assignee)'). Alleen `updates` mergen laat
      // die lookups op de oude waarde staan — dan lijkt een net gekoppelde uitvoerder
      // niet opgeslagen. Lege velden ontbreken in de response, dus overschrijven we
      // alleen de lookups en laten de expliciet gezette waarden uit `updates` winnen.
      const data = await response.json().catch(() => null);
      const verse = data?.task?.fields;
      const LOOKUP_VELDEN = ['Voornaam Assignee', 'Functie (from Assignee)'];
      const mergeFields = (fields) => {
        const samen = { ...fields, ...updates };
        if (verse) {
          LOOKUP_VELDEN.forEach(k => {
            if (verse[k] !== undefined) samen[k] = verse[k];
            else delete samen[k];
          });
        }
        return samen;
      };

      // Update local state
      setTasks(prev => prev.map(t =>
        t.id === taskId
          ? { ...t, fields: mergeFields(t.fields) }
          : t
      ));

      // Also update selectedTask if it's the same task (so dialog shows updated data)
      setSelectedTask(prev =>
        prev && prev.id === taskId
          ? { ...prev, fields: mergeFields(prev.fields) }
          : prev
      );

      return true;
    } catch (err) {
      console.error('Error updating task:', err);
      toast.error(`Fout bij bijwerken: ${err.message}`);
      return false;
    }
  };

  // Handle toggle task completion (from checkbox click)
  // `metOngedaan: false` voor bulk-acties (anders één undo-toast per taak).
  const handleToggleComplete = async (task, completed, { metOngedaan = true } = {}) => {
    const taskId = task.id;
    const updates = { Afgerond: completed };

    // If marking as complete, also update PrioNr to 'Afgerond'
    if (completed) {
      updates.PrioNr = 'Afgerond';
    }

    // Vorige waarden vastleggen vóór de PATCH — voor "Ongedaan maken".
    const vorige = {
      Afgerond: Boolean(task.fields?.Afgerond),
      PrioNr: task.fields?.PrioNr ?? null,
    };

    const ok = await handleTaskUpdate(taskId, updates);
    if (ok && metOngedaan) {
      biedOngedaanAan(completed ? 'Taak afgevinkt' : 'Taak weer open', async () => {
        if (!(await handleTaskUpdate(taskId, vorige))) throw new Error('Terugzetten mislukt');
      });
    }
    return ok;
  };

  // Alleen de notities verversen (na "Hele gesprek als notitie"), zonder de pagina-skeleton
  // van loadData; de Node-statusroute heeft de project-overview-cache dan al gewist.
  const vernieuwNotities = async () => {
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/project-overview/${id}`);
      if (!response.ok) return;
      const data = await response.json();
      setNotes(data.notes || []);
    } catch { /* volgende laadronde haalt ze alsnog op */ }
  };

  const loadData = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await authFetch(`${BACKEND_URL}/Api/project-overview/${id}`);
      if (!response.ok) {
        const d = await response.json().catch(() => ({}));
        throw new Error(d.error || `HTTP ${response.status}`);
      }
      const data = await response.json();
      setProject(data.project || null);
      setTasks(data.tasks || []);
      setDeliverables(data.deliverables || []);
      setNotes(data.notes || []);
      setQuitclaims(data.quitclaims || []);
      setDraaidagen(data.draaidagen || []);
      setCallsheets(data.callsheets || []);
    } catch (err) {
      setError(`Kon project niet laden: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadData();
  }, [id]);

  // Einde-draaidag-rapporten (briefings voor de editor). Bewust los van
  // loadData: de engine kan slapen (test schaalt naar 0) en dat mag het
  // projectoverzicht niet ophouden.
  useEffect(() => {
    if (!id) return;
    let actueel = true;
    const loadEindeDraaidagen = async () => {
      try {
        const resp = await authFetch(`${BACKEND_URL}/Api/project-overview/${id}/einde-draaidagen`);
        if (!resp.ok) return;
        const data = await resp.json();
        if (actueel) setEindeDraaidagen(data.rapporten || []);
      } catch { /* ignore - draaidagen blijven gewoon zichtbaar */ }
    };
    loadEindeDraaidagen();
    return () => { actueel = false; };
  }, [id]);

  // Meetings: gekoppeld (feit, telt mee) + suggesties (klantnaam-matching in de
  // titel — zoeken op projectnummer gaf altijd 0, daarom stond de teller op 0
  // terwijl er wél opnames van de klant waren).
  const projectNumber = project?.fields?.Projectnummer || '';
  const projectName = project?.fields?.Projectnaam || '';
  const klantRaw = project?.fields?.KlantnaamString;
  const klantNaam = Array.isArray(klantRaw) ? (klantRaw[0] || '') : (klantRaw || '');

  const loadMeetings = useCallback(async () => {
    if (!id) return;
    try {
      const params = new URLSearchParams();
      if (klantNaam) params.set('klant', klantNaam);
      if (projectName) params.set('projectNaam', projectName);
      const response = await authFetch(`${BACKEND_URL}/Api/project-flow/${id}/meetings?${params.toString()}`);
      if (!response.ok) return;
      const data = await response.json();
      setTranscripts(data?.gekoppeld || []);
      setMeetingSuggesties(data?.suggesties || []);
    } catch { /* ignore */ }
  }, [id, klantNaam, projectName]);

  useEffect(() => { loadMeetings(); }, [loadMeetings]);

  // Load Frame.io videos
  useEffect(() => {
    if (!projectNumber) return;
    const loadFrameioVideos = async () => {
      try {
        const resp = await authFetch(`${AI_ENGINE_URL}/api/v1/frameio/videos/project/${encodeURIComponent(projectNumber)}`);
        if (!resp.ok) return;
        const data = await resp.json();
        setFrameioVideos(data.videos || []);
      } catch { /* ignore - feature may not be available yet */ }
    };
    loadFrameioVideos();
  }, [projectNumber]);

  // Offerteregels uit de lokale spiegel (Airtable ← Gripp). Bewust hier en niet in
  // /Api/project-overview: die call gaat naar Airtable en is al de traagste stap.
  const [quoteLines, setQuoteLines] = useState(null);
  const [quoteLinesLoading, setQuoteLinesLoading] = useState(false);
  const [quoteLinesError, setQuoteLinesError] = useState(false);
  useEffect(() => {
    if (!projectNumber) return;
    let geannuleerd = false;
    const loadQuoteLines = async () => {
      setQuoteLinesLoading(true);
      setQuoteLinesError(false);
      try {
        // Met het record-id: de engine koppelt regels aan dít project, niet aan het nummer alleen.
        const resp = await authFetch(`${AI_ENGINE_URL}/api/v1/quote-lines/project/${encodeURIComponent(projectNumber)}?airtable_project_id=${encodeURIComponent(id || '')}`);
        if (!resp.ok) throw new Error(`HTTP ${resp.status}`);
        const data = await resp.json();
        if (!geannuleerd) setQuoteLines(data);
      } catch {
        if (!geannuleerd) setQuoteLinesError(true);
      } finally {
        if (!geannuleerd) setQuoteLinesLoading(false);
      }
    };
    loadQuoteLines();
    return () => { geannuleerd = true; };
  }, [projectNumber, id]);

  // Klaargezet taakvoorstel (na acceptatie) voor dit project — draft-only banner.
  const [pendingProposal, setPendingProposal] = useState(null);
  useEffect(() => {
    if (!projectNumber) return;
    (async () => {
      try {
        const r = await authFetch(`${BACKEND_URL}/Api/tasks/proposals?status=pending&project_number=${encodeURIComponent(projectNumber)}`);
        if (!r.ok) return;
        const d = await r.json();
        setPendingProposal((d.proposals || [])[0] || null);
      } catch { /* niet kritiek */ }
    })();
  }, [projectNumber]);

  const markProposal = async (status) => {
    if (!pendingProposal) return;
    try {
      await authFetch(`${BACKEND_URL}/Api/tasks/proposals/${pendingProposal.id}/status`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ status }),
      });
    } catch { /* niet kritiek */ }
    setPendingProposal(null);
  };

  // Load QA reviews for this project (uses project_number; API also supports project_fk_id)
  useEffect(() => {
    if (!projectNumber) return;
    const loadQaReviews = async () => {
      try {
        const params = new URLSearchParams();
        params.set('project_number', projectNumber);
        const resp = await authFetch(`${AI_ENGINE_URL}/api/v1/video-search/qa/reviews?${params.toString()}`);
        if (!resp.ok) return;
        const data = await resp.json();
        setQaReviews(data.reviews || []);
      } catch { /* ignore */ }
    };
    loadQaReviews();
  }, [projectNumber]);

  // Rapporten bij hun draaidag zoeken. Een rapport waarvan de draaidag hier niet
  // in de lijst staat (gefilterd weg, of alleen aan het project gekoppeld) mag
  // niet verdwijnen — die tonen we los onder de lijst.
  const { rapportenPerDraaidag, losseRapporten } = useMemo(() => {
    const bekendeDagen = new Set(draaidagen.map(d => d.id));
    const perDag = new Map();
    const los = [];
    for (const r of eindeDraaidagen) {
      if (r.draaidagId && bekendeDagen.has(r.draaidagId)) {
        perDag.set(r.draaidagId, [...(perDag.get(r.draaidagId) || []), r]);
      } else {
        los.push(r);
      }
    }
    return { rapportenPerDraaidag: perDag, losseRapporten: los };
  }, [draaidagen, eindeDraaidagen]);

  // "Hele gesprek als notitie" (Correspondentie) draait op de server; de status volgen we
  // hier op paginaniveau, zodat hij doorloopt als de drawer dicht gaat en daarna de
  // notities (teller + Notes) ververst.
  const mailNotities = useMailNotities(id, { onKlaar: vernieuwNotities });

  const value = {
    id,
    userEmail,
    loading,
    error,
    loadData,
    mailNotities,
    project,
    setProject,
    projectNumber,
    projectName,
    tasks,
    deliverables,
    setDeliverables,
    notes,
    transcripts,
    meetingSuggesties,
    loadMeetings,
    quitclaims,
    draaidagen,
    callsheets,
    eindeDraaidagen,
    frameioVideos,
    downloadingVideoId,
    qaReviews,
    personen,
    quoteLines,
    quoteLinesLoading,
    quoteLinesError,
    pendingProposal,
    markProposal,
    selectedTask,
    setSelectedTask,
    autoEditTaskDate,
    setAutoEditTaskDate,
    newNoteText,
    setNewNoteText,
    savingNote,
    newTaskText,
    setNewTaskText,
    savingTask,
    handleVideoDownload,
    handleAddNote,
    handleUpdateNote,
    handleDeleteNote,
    handleAddQuickTask,
    handleTaskUpdate,
    handleTaskDelete,
    handleToggleComplete,
    tasksPerDeliverable,
    rapportenPerDraaidag,
    losseRapporten,
  };

  return (
    <ProjectDataContext.Provider value={value}>
      {children}
    </ProjectDataContext.Provider>
  );
}

/** Zelfde context, maar zonder fout buiten de provider (taakregels in losse lijsten en tests). */
export function useProjectDataOptioneel() {
  return useContext(ProjectDataContext);
}

export function useProjectData() {
  const ctx = useContext(ProjectDataContext);
  if (!ctx) {
    throw new Error('useProjectData moet binnen een ProjectDataProvider gebruikt worden');
  }
  return ctx;
}
